import { createContext, useContext } from 'react';
import { makeAutoObservable } from 'mobx';
import { FileInfo, FileType } from 'types/policyService';
import { getPolicyByEnrollmentPeriodId } from 'api/operations/policyService';
import { retryRequestFor } from 'global/utils/retryRequestFor';

export default class ProductOutputsPageStore {
  private _isLoading = true;
  private _isWarningModalOpen = false;
  private _proposalOutputs: FileInfo[] = [];

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  private get proposalOutputs(): FileInfo[] {
    return this._proposalOutputs;
  }

  public get benefitSummaries(): FileInfo[] {
    return this.proposalOutputs.filter((file) => file.type === FileType.BENEFIT_SUMMARY);
  }

  public get productsJson(): FileInfo[] {
    return this.proposalOutputs.filter((file) => file.type === FileType.PRODUCT_JSON);
  }

  public get benefitsZip(): FileInfo | null {
    return this.proposalOutputs.find((file) => file.type === FileType.BENEFIT_SUMMARY_ZIP) || null;
  }

  public get jsonZip(): FileInfo | null {
    return this.proposalOutputs.find((file) => file.type === FileType.PRODUCT_JSON_ZIP) || null;
  }

  public get creationDate(): string {
    return this.proposalOutputs.length > 0 ? this.proposalOutputs[0].creationDate : '';
  }

  public get isWarningModalOpen(): boolean {
    return this._isWarningModalOpen;
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private setProposalOutputs(outputs: FileInfo[]): void {
    this._proposalOutputs = outputs;
  }

  public setWarningModal(status: boolean): void {
    this._isWarningModalOpen = status;
  }

  // Api calls
  public async getInitialData(periodId: string): Promise<void> {
    this.resetInitialData();
    this.setIsLoading(true);
    try {
      const policy = await retryRequestFor(() => getPolicyByEnrollmentPeriodId(periodId), 10000, 2000);
      this.setProposalOutputs(policy.productOutputs);
    } catch (e) {
      this.setWarningModal(true);
    } finally {
      this.setIsLoading(false);
    }
  }

  public resetInitialData(): void {
    this.setIsLoading(true);
    this.setProposalOutputs([]);
    this.setWarningModal(false);
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const ProductOutputsPageStoreContext = createContext(new ProductOutputsPageStore());

export const useProductOutputsPageStore = (): ProductOutputsPageStore => {
  return useContext(ProductOutputsPageStoreContext);
};

export { ProductOutputsPageStoreContext };
